import {
    CheckIcon,
    ChevronDownIcon,
    MagnifyingGlassIcon,
} from '@radix-ui/react-icons';
import {
    Popover,
    TextField,
    Flex,
    Text,
    Button,
    ScrollArea,
    Box,
} from '@radix-ui/themes';
import { useState, useMemo } from 'react';
import { useFormContext, Controller, FieldValues, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ComboboxInputConfig } from '../../types';
import { FieldWrapper } from '../FieldWrapper';

export const ComboboxField = <TFormValues extends FieldValues>({
    config,
}: {
    config: ComboboxInputConfig<TFormValues>;
}) => {
    const { control } = useFormContext<TFormValues>();
    const { t } = useTranslation('dynamicForm');

    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');

    const filteredOptions = useMemo(() => {
        if (!search) return config.options;
        const lowerSearch = search.toLowerCase();
        return config.options.filter((opt) =>
            opt.label.toLowerCase().includes(lowerSearch),
        );
    }, [config.options, search]);

    return (
        <FieldWrapper config={config}>
            <Controller
                name={config.name as Path<TFormValues>}
                control={control}
                rules={config.validation}
                render={({ field }) => {
                    const safeValue = field.value?.toString() ?? '';

                    const selectedOption = config.options.find(
                        (opt) => opt.value.toString() === safeValue,
                    );

                    const handleSelect = (selectedValue: string) => {
                        field.onChange(selectedValue);
                        setOpen(false);
                        setSearch('');
                    };

                    return (
                        <Popover.Root open={open} onOpenChange={setOpen}>
                            <Popover.Trigger>
                                <Button
                                    variant='surface'
                                    color='gray'
                                    role='combobox'
                                    aria-expanded={open}
                                    style={{
                                        width: '100%',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Text
                                        color={
                                            selectedOption ? undefined : 'gray'
                                        }
                                        weight='regular'
                                    >
                                        {selectedOption?.label ??
                                            config.placeholder ??
                                            t('combobox.select_placeholder')}
                                    </Text>
                                    <ChevronDownIcon />
                                </Button>
                            </Popover.Trigger>

                            <Popover.Content
                                width='var(--radix-popover-trigger-width)'
                                style={{ padding: 0, overflow: 'hidden' }}
                            >
                                <Flex
                                    direction='column'
                                    style={{ maxHeight: '300px' }}
                                >
                                    <Box
                                        p='2'
                                        style={{
                                            borderBottom:
                                                '1px solid var(--gray-a4)',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <TextField.Root
                                            placeholder={t(
                                                'combobox.search_placeholder',
                                            )}
                                            value={search}
                                            onChange={(e) =>
                                                setSearch(e.target.value)
                                            }
                                        >
                                            <TextField.Slot>
                                                <MagnifyingGlassIcon
                                                    height='16'
                                                    width='16'
                                                />
                                            </TextField.Slot>
                                        </TextField.Root>
                                    </Box>

                                    <ScrollArea
                                        type='auto'
                                        scrollbars='vertical'
                                        style={{ flex: 1 }}
                                        asChild
                                    >
                                        <Flex direction='column' p='1'>
                                            {filteredOptions.length === 0 ? (
                                                <Text
                                                    size='2'
                                                    color='gray'
                                                    align='center'
                                                >
                                                    {t('combobox.no_results')}
                                                </Text>
                                            ) : (
                                                filteredOptions.map((opt) => {
                                                    const isSelected =
                                                        opt.value.toString() ===
                                                        safeValue;

                                                    return (
                                                        <Button
                                                            key={opt.value}
                                                            variant='ghost'
                                                            color='gray'
                                                            onClick={() =>
                                                                handleSelect(
                                                                    opt.value.toString(),
                                                                )
                                                            }
                                                            style={{
                                                                width: '100%',
                                                                justifyContent:
                                                                    'flex-start',
                                                            }}
                                                        >
                                                            <Flex
                                                                width='100%'
                                                                justify='between'
                                                                align='center'
                                                                gap='3'
                                                                px={'2'}
                                                            >
                                                                <Text
                                                                    weight={
                                                                        isSelected
                                                                            ? 'bold'
                                                                            : 'regular'
                                                                    }
                                                                >
                                                                    {opt.label}
                                                                </Text>
                                                                {isSelected && (
                                                                    <CheckIcon />
                                                                )}
                                                            </Flex>
                                                        </Button>
                                                    );
                                                })
                                            )}
                                        </Flex>
                                    </ScrollArea>
                                </Flex>
                            </Popover.Content>
                        </Popover.Root>
                    );
                }}
            />
        </FieldWrapper>
    );
};
